import React, { useEffect, useRef, useState } from "react";
import {
  Typography,
  Button,
  Box,
  Drawer,
  Divider,
  makeStyles,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  CircularProgress,
  AppBar,
  Toolbar,
  useTheme,
  useMediaQuery,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField
} from '@material-ui/core';
import {
  Timeline,
  Edit,
  Save,
  Menu as MenuIcon,
  Delete
} from '@material-ui/icons';
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "@geoman-io/leaflet-geoman-free";
import "@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css";
import axios from 'axios';
import { connect } from 'react-redux';

const DRAWER_WIDTH = 300;

// Define styles first
const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    height: '100vh',
    overflow: 'hidden'
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
  },
  drawer: {
    width: DRAWER_WIDTH,
    flexShrink: 0,
  },
  drawerPaper: {
    width: DRAWER_WIDTH,
    padding: theme.spacing(2),
    paddingTop: theme.spacing(10)
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(1),
    paddingTop: theme.spacing(8), // Space for the app bar
    height: '100vh',
    overflow: 'hidden',
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: -DRAWER_WIDTH,
  },
  contentShift: {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  },
  mapContainer: {
    height: 'calc(100vh - 80px)',
    width: '100%',
    border: '1px solid #ccc',
    borderRadius: 4,
    position: 'relative',
    overflow: 'hidden',
    zIndex: 1
  },
  mapWrapper: {
    position: 'relative',
    width: '100%',
    height: '100%'
  },
  buttonGroup: {
    marginBottom: theme.spacing(2)
  },
  colorIndicator: {
    width: 16,
    height: 16,
    borderRadius: '50%',
    marginRight: theme.spacing(1)
  },
  sectionHeader: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1)
  },
  '@global': {
    '.polygon-label': {
      background: 'transparent',
      border: 'none',
      boxShadow: 'none',
      fontWeight: 'bold',
      fontSize: '12px',
      color: '#000',
      textShadow: '1px 1px 0 white, -1px -1px 0 white, 1px -1px 0 white, -1px 1px 0 white',
      pointerEvents: 'none' // Make sure it doesn't interfere with clicks
    }
  }
}));

const addLabelToPolygon = (layer, territoryName) => {
  const labelText = territoryName || 'Unnamed Territory';
  layer.bindTooltip(labelText, {
    permanent: true,
    direction: 'center',
    className: 'polygon-label'
  });
};

const generateDistinctColorMap = (territories) => {
  const colorMap = {};
  const visuallyDistinctOrder = [0, 180, 90, 270, 45, 225, 135, 315];
  territories.forEach((territory, index) => {
    const hue = visuallyDistinctOrder[index % visuallyDistinctOrder.length];
    colorMap[territory._id] = `hsl(${hue}, 70%, 50%)`;
  });
  return colorMap;
};

const HerbicideTerritoryMap = (props) => {
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const drawnItemsRef = useRef(null);

  // State
  const [selectedPolygon, setSelectedPolygon] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [polygons, setPolygons] = useState([]);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '' });
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [sprayTechs, setSprayTechs] = useState([]);
  const [isSnappingEnabled, setIsSnappingEnabled] = useState(true);
  const [drawingMode, setDrawingMode] = useState(false);
  const [editingMode, setEditingMode] = useState(false);
  const editingModeRef = useRef(false);
  const drawingModeRef = useRef(false);
  const [territoryColorMap, setTerritoryColorMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedTerritoryInList, setSelectedTerritoryInList] = useState(null);
  const [territoryName, setTerritoryName] = useState('');
  const [territories, setTerritories] = useState([]);

  // Responsive behavior
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Apply styles
  const classes = useStyles();

  // Use effect to handle mobile drawer state
  useEffect(() => {
    setDrawerOpen(!isMobile);
  }, [isMobile]);

  // Initialize map
  useEffect(() => {
    // Load any existing territories from the server
    fetchTerritories();

    if (mapContainerRef.current && !mapRef.current) {
      initializeMap();
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        drawnItemsRef.current = null;
      }
    };
  }, []);

  // Keep refs in sync for Leaflet click handlers (stale closure fix)
  useEffect(() => { editingModeRef.current = editingMode; }, [editingMode]);
  useEffect(() => { drawingModeRef.current = drawingMode; }, [drawingMode]);

  // Invalidate map size when drawer opens/closes
  useEffect(() => {
    if (mapRef.current) {
      // Give time for the drawer animation to complete
      setTimeout(() => {
        mapRef.current.invalidateSize();
      }, 300);
    }
  }, [drawerOpen]);

  // Initialize the map and its controls
  const initializeMap = () => {
    // Create the map with Google Hybrid layer (streets + satellite)
    mapRef.current = L.map(mapContainerRef.current, {
      center: [33.55, -112.0], // Default center (Phoenix, AZ)
      zoom: 12,
      zoomControl: true,
    });

    // Add Google Hybrid layer for street names visibility
    L.tileLayer("https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}", {
      attribution: "&copy; Google Maps",
      maxZoom: 20,
      minZoom: 0
    }).addTo(mapRef.current);

    // Initialize the feature group to store drawn items
    const drawnItems = new L.FeatureGroup();
    mapRef.current.addLayer(drawnItems);
    drawnItemsRef.current = drawnItems;

    // Initialize Leaflet-Geoman with global options
    mapRef.current.pm.setGlobalOptions({
      // Snapping settings
      snappable: isSnappingEnabled,
      snapDistance: 20, // Distance in pixels for snapping
      snapSegment: true, // Allow snapping to segments, not just vertices

      // Prevent overlapping
      allowSelfIntersection: false,

      // Visual settings
      templineStyle: {
        color: '#3388ff',
      },
      hintlineStyle: {
        color: '#3388ff',
        dashArray: [5, 5],
      },
      pathOptions: {
        color: '#3388ff',
        fillColor: '#3388ff',
        fillOpacity: 0.2,
      }
    });

    // Set up event handlers for Leaflet-Geoman
    mapRef.current.on('pm:create', (e) => {
      const layer = e.layer;

      // Add the created polygon to our feature group
      drawnItems.addLayer(layer);

      layer.territoryName = 'New Territory';
      layer.assignedEmployee = null;

      layer.on('click', (event) => {
        L.DomEvent.stopPropagation(event);
        if (!editingModeRef.current && !drawingModeRef.current) {
          setSelectedPolygon(layer);
          setTerritoryName(layer.territoryName || '');
          setSelectedEmployee(layer.assignedEmployee || '');
          setDialogOpen(true);
        }
      });

      disableDrawMode();

      // Open dialog immediately for naming
      setSelectedPolygon(layer);
      setTerritoryName('New Territory');
      setSelectedEmployee('');
      setDialogOpen(true);
    });

    // Handle edited polygons
    mapRef.current.on('pm:update', () => {
      // Update polygons in state after edit
      updateTerritoriesState();

      setNotification({
        open: true,
        message: 'Polygon edited! Remember to save your changes.'
      });
    });

    // Handle removed polygons
    mapRef.current.on('pm:remove', () => {
      // Update polygons in state after removal
      updateTerritoriesState();

      setNotification({
        open: true,
        message: 'Polygon removed! Remember to save your changes.'
      });
    });
  };

  // Toggle snapping functionality
  const toggleSnapping = () => {
    const newSnappingState = !isSnappingEnabled;
    setIsSnappingEnabled(newSnappingState);

    // Update Leaflet-Geoman global options
    if (mapRef.current && mapRef.current.pm) {
      mapRef.current.pm.setGlobalOptions({
        snappable: newSnappingState
      });
    }

    setNotification({
      open: true,
      message: `Snapping ${newSnappingState ? 'enabled' : 'disabled'}`
    });
  };

  // Fetch existing territories from the server
  const fetchTerritories = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${props.url}/api/hmi/genericGetRequest?type=HERBICIDE_TERRITORY_DETAILS`);
      const { existingTerritories, sprayTechs: fetchedSprayTechs } = response?.data?.data;

      if (fetchedSprayTechs && fetchedSprayTechs.length) {
        setSprayTechs(fetchedSprayTechs);
      }

      let colorMap = {};

      if (existingTerritories && existingTerritories.length) {
        colorMap = generateDistinctColorMap(existingTerritories);
        setTerritoryColorMap(colorMap);

        const transformedTerritories = existingTerritories.map(territory => {
          const coordinates = territory.geometry.coordinates.map(ring =>
            ring.map(coord => ({
              lng: coord[0],
              lat: coord[1]
            }))
          );
          return {
            _id: territory._id,
            name: territory.name || 'Unnamed Territory',
            assignedEmployee: territory.assignedEmployee,
            coordinates,
            active: territory.active
          };
        });

        setTerritories(transformedTerritories);
        setPolygons(transformedTerritories);

        if (mapRef.current && drawnItemsRef.current) {
          drawnItemsRef.current.clearLayers();

          transformedTerritories.forEach(polygon => {
            const latLngs = polygon.coordinates.map(ring =>
              ring.map(coord => L.latLng(coord.lat, coord.lng))
            );
            const layer = L.polygon(latLngs);
            layer._id = polygon._id;
            layer.territoryName = polygon.name;
            layer.assignedEmployee = polygon.assignedEmployee;

            layer.setStyle({
              color: colorMap[polygon._id] || '#3388ff',
              fillOpacity: 0.3
            });

            addLabelToPolygon(layer, polygon.name);

            layer.on('click', (event) => {
              L.DomEvent.stopPropagation(event);
              if (!editingModeRef.current && !drawingModeRef.current) {
                setSelectedPolygon(layer);
                setTerritoryName(layer.territoryName || '');
                setSelectedEmployee(layer.assignedEmployee || '');
                setDialogOpen(true);
              }
            });

            drawnItemsRef.current.addLayer(layer);
            layer.pm.enable({ allowSelfIntersection: false, snappable: isSnappingEnabled, snapDistance: 20 });
            layer.pm.disable();
          });
        }
      } else {
        setTerritories([]);
        setPolygons([]);
      }
    } catch (error) {
      console.error('Error fetching territories:', error);
      setNotification({ open: true, message: 'Failed to load existing territories' });
    } finally {
      setLoading(false);
    }
  };

  // Update the territories state based on what's currently drawn on the map
  const updateTerritoriesState = () => {
    if (!drawnItemsRef.current) return;
    const current = [];
    drawnItemsRef.current.eachLayer(layer => {
      if (layer instanceof L.Polygon) {
        current.push({
          _id: layer._id || layer._leaflet_id,
          name: layer.territoryName || 'Unnamed Territory',
          assignedEmployee: layer.assignedEmployee || null
        });
      }
    });
    setTerritories(current);
  };

  // Helper to extract coordinates from Leaflet's nested LatLng objects
  const extractLatLngs = (latLngs) => {
    // Handle nested arrays (multi-polygons)
    if (latLngs[0] instanceof Array) {
      return latLngs.map(ring =>
        ring.map(latlng => ({ lat: latlng.lat, lng: latlng.lng }))
      );
    }
    // Handle simple polygons
    return [latLngs.map(latlng => ({ lat: latlng.lat, lng: latlng.lng }))];
  };

  // Assign territory details to the selected polygon
  const assignTerritory = () => {
    if (!selectedPolygon) return;

    selectedPolygon.territoryName = territoryName || 'Unnamed Territory';
    selectedPolygon.assignedEmployee = selectedEmployee || null;

    // Update label
    if (selectedPolygon.getTooltip()) {
      selectedPolygon.unbindTooltip();
    }
    addLabelToPolygon(selectedPolygon, selectedPolygon.territoryName);

    updateTerritoriesState();
    setDialogOpen(false);
  };

  // Delete the selected territory polygon
  const deleteTerritory = () => {
    if (!selectedPolygon || !drawnItemsRef.current) return;
    drawnItemsRef.current.removeLayer(selectedPolygon);
    updateTerritoriesState();
    setDialogOpen(false);
    setSelectedPolygon(null);
  };

  const saveTerritories = async () => {
    if (!drawnItemsRef.current) return;
    try {
      setSaving(true);
      const allTerritories = [];

      drawnItemsRef.current.eachLayer(layer => {
        if (layer instanceof L.Polygon) {
          const latLngs = layer.getLatLngs()[0];
          const coordinates = [latLngs.map(latLng => ({
            lat: latLng.lat,
            lng: latLng.lng
          }))];

          allTerritories.push({
            name: layer.territoryName || 'Unnamed Territory',
            coordinates,
            assignedEmployee: layer.assignedEmployee || null
          });
        }
      });

      const response = await axios.post(`${props.url}/api/hmi/genericPostRequest`, allTerritories, {
        params: { type: 'SAVE_HERBICIDE_TERRITORIES' }
      });

      if (response.data.success) {
        setNotification({ open: true, message: 'Territories saved successfully' });
        fetchTerritories();
      } else {
        throw new Error(response.data.message || 'Failed to save territories');
      }
    } catch (error) {
      console.error('Error saving territories:', error);
      setNotification({ open: true, message: `Failed to save territories: ${error.message}` });
    } finally {
      setSaving(false);
    }
  };

  // Clear all drawn polygons
  const clearDrawings = () => {
    if (drawnItemsRef.current) {
      drawnItemsRef.current.clearLayers();
      setPolygons([]);
      setTerritories([]);

      // Make sure to disable any active editing or drawing
      disableDrawMode();
      disableEditMode();
      setSelectedTerritoryInList(null);

      setNotification({
        open: true,
        message: 'All polygons cleared'
      });
    }
  };

  // Handle the draw polygon button click - Enable draw mode
  const handleDrawPolygon = () => {
    if (mapRef.current) {
      // Disable edit mode if active
      disableEditMode();

      // Enable draw mode
      mapRef.current.pm.enableDraw('Polygon', {
        snappable: isSnappingEnabled,
        snapDistance: 20,
        allowSelfIntersection: false,
        finishOn: 'dblclick', // Finish drawing on double click
        templineStyle: {
          color: '#3388ff',
        },
        hintlineStyle: {
          color: '#3388ff',
          dashArray: [5, 5],
        },
        pathOptions: {
          color: '#3388ff',
          fillColor: '#3388ff',
          fillOpacity: 0.2,
        }
      });

      setDrawingMode(true);

      // If on mobile, close the drawer when starting to draw
      if (isMobile && drawerOpen) {
        setDrawerOpen(false);
      }

      setNotification({
        open: true,
        message: 'Draw mode enabled. Click to create vertices, double-click to finish.'
      });
    }
  };

  // Disable draw mode
  const disableDrawMode = () => {
    if (mapRef.current) {
      mapRef.current.pm.disableDraw();
      setDrawingMode(false);
    }
  };

  // Handle the edit polygons button click - Enable edit mode
  const handleEditPolygons = () => {
    if (mapRef.current && drawnItemsRef.current) {
      // Disable draw mode if active
      disableDrawMode();

      // Enable edit mode for all layers
      drawnItemsRef.current.eachLayer(layer => {
        if (layer instanceof L.Polygon) {
          layer.pm.enable({
            snappable: isSnappingEnabled,
            snapDistance: 20,
            allowSelfIntersection: false,
          });
        }
      });

      setEditingMode(true);

      // If on mobile, close the drawer when starting to edit
      if (isMobile && drawerOpen) {
        setDrawerOpen(false);
      }

      setNotification({
        open: true,
        message: 'Edit mode enabled. Click and drag vertices to reshape polygons.'
      });
    }
  };

  // Disable edit mode
  const disableEditMode = () => {
    if (mapRef.current && drawnItemsRef.current) {
      drawnItemsRef.current.eachLayer(layer => {
        if (layer instanceof L.Polygon && layer.pm) {
          layer.pm.disable();

          // Restore original styling based on territory
          const color = territoryColorMap[layer._id] || '#3388ff';
          layer.setStyle({
            color: color,
            fillOpacity: 0.3,
            weight: 3
          });
        }
      });

      setEditingMode(false);
      setSelectedTerritoryInList(null);
    }
  };

  // Toggle edit mode
  const toggleEditMode = () => {
    if (editingMode) {
      disableEditMode();
      setNotification({
        open: true,
        message: 'Edit mode disabled.'
      });
    } else {
      handleEditPolygons();
    }
  };

  const handleTerritoryPress = (territory) => {
    // If the same territory is clicked again, deselect it
    if (selectedTerritoryInList === territory._id) {
      // Deselect all polygons
      if (drawnItemsRef.current) {
        drawnItemsRef.current.eachLayer(layer => {
          if (layer instanceof L.Polygon) {
            // Restore original styling
            const color = territoryColorMap[layer._id] || '#3388ff';
            layer.setStyle({
              color: color,
              fillOpacity: 0.3,
              weight: 3
            });

            // Disable editing for this polygon
            if (layer.pm) {
              layer.pm.disable();
            }
          }
        });
      }

      setSelectedTerritoryInList(null);
      setNotification({
        open: true,
        message: 'Territory selection cleared'
      });

      // Disable edit mode
      setEditingMode(false);
      return;
    }

    // If we're in drawing mode, disable it
    disableDrawMode();

    // Enable edit mode if it's not already enabled
    if (!editingMode) {
      setEditingMode(true);
    }

    // Select the territory
    setSelectedTerritoryInList(territory._id);

    // Highlight and make editable only this territory's polygon
    if (drawnItemsRef.current) {
      let foundPolygons = 0;

      drawnItemsRef.current.eachLayer(layer => {
        if (layer instanceof L.Polygon) {
          const layerId = layer._id || layer._leaflet_id;
          if (layerId === territory._id) {
            // Highlight this territory's polygon
            layer.setStyle({
              color: territoryColorMap[territory._id] || '#3388ff',
              fillOpacity: 0.5,
              weight: 5
            });

            // Enable editing for this polygon
            if (layer.pm) {
              layer.pm.enable({
                snappable: isSnappingEnabled,
                snapDistance: 20,
                allowSelfIntersection: false,
              });
            }

            // Zoom to the polygon
            const bounds = layer.getBounds();
            if (bounds.isValid()) {
              mapRef.current.fitBounds(bounds, { padding: [50, 50] });
            }

            foundPolygons++;
          } else {
            // Fade out other polygons
            layer.setStyle({
              color: '#999999',
              fillOpacity: 0.1,
              weight: 1
            });

            // Disable editing for other polygons
            if (layer.pm) {
              layer.pm.disable();
            }
          }
        }
      });

      // If on mobile, close the drawer when selecting polygons
      if (isMobile && drawerOpen) {
        setDrawerOpen(false);
      }

      setNotification({
        open: true,
        message: `Selected ${foundPolygons} polygon${foundPolygons !== 1 ? 's' : ''} for territory "${territory.name}"`
      });
    }
  };

  // Component JSX
  return (
    <div className={classes.root}>
      {/* App Bar */}
      <AppBar position="fixed" className={classes.appBar}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={() => setDrawerOpen(!drawerOpen)}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap>
            Herbicide Territory Map
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Drawer
        className={classes.drawer}
        variant={isMobile ? "temporary" : "persistent"}
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        classes={{
          paper: classes.drawerPaper,
        }}
      >
        {/* Map Actions */}
        <Typography variant="h6" className={classes.sectionHeader}>Map Tools</Typography>
        <div>
          <Button
            variant={drawingMode ? "contained" : "outlined"}
            color="primary"
            fullWidth
            startIcon={<Timeline />}
            onClick={drawingMode ? disableDrawMode : handleDrawPolygon}
            className={classes.buttonGroup}
          >
            {drawingMode ? 'Cancel Drawing' : 'Draw Polygon'}
          </Button>

          <Button
            variant={editingMode ? "contained" : "outlined"}
            color="primary"
            fullWidth
            startIcon={<Edit />}
            onClick={toggleEditMode}
            className={classes.buttonGroup}
          >
            {editingMode ? 'Finish Editing' : 'Edit Polygons'}
          </Button>

          <Button
            variant="outlined"
            color="secondary"
            fullWidth
            startIcon={<Delete />}
            onClick={clearDrawings}
            className={classes.buttonGroup}
          >
            Clear All
          </Button>

          {/* Toggle Snapping Button */}
          <Button
            variant={isSnappingEnabled ? "contained" : "outlined"}
            color="primary"
            fullWidth
            onClick={toggleSnapping}
            className={classes.buttonGroup}
          >
            Snapping: {isSnappingEnabled ? 'ON' : 'OFF'}
          </Button>
        </div>

        <Divider />

        {/* Territory Legend */}
        <Typography variant="h6" className={classes.sectionHeader}>
          Herbicide Territories:
        </Typography>
        {loading ? (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress size={24} />
          </Box>
        ) : territories.length === 0 ? (
          <Typography variant="body2" color="textSecondary" style={{ padding: '8px 16px' }}>
            Draw your first territory to get started
          </Typography>
        ) : (
          <List dense>
            {territories.map(territory => {
              const emp = sprayTechs.find(e => String(e._id) === String(territory.assignedEmployee));
              const empName = emp ? `${emp.firstName} ${emp.lastName}` : 'Unassigned';
              return (
                <ListItem
                  key={territory._id}
                  onClick={() => handleTerritoryPress(territory)}
                  style={{
                    cursor: 'pointer',
                    backgroundColor: selectedTerritoryInList === territory._id ? 'rgba(0, 0, 0, 0.08)' : 'transparent'
                  }}
                  selected={selectedTerritoryInList === territory._id}
                >
                  <div
                    className={classes.colorIndicator}
                    style={{
                      backgroundColor: territoryColorMap[territory._id] || '#3388ff',
                      width: selectedTerritoryInList === territory._id ? 20 : 16,
                      height: selectedTerritoryInList === territory._id ? 20 : 16
                    }}
                  />
                  <ListItemText primary={territory.name} secondary={empName} />
                </ListItem>
              );
            })}
          </List>
        )}

        <Divider />

        {/* Save Button */}
        <Box mt={3} mb={2}>
          <Button
            variant="contained"
            color="secondary"
            fullWidth
            startIcon={saving ? <CircularProgress size={24} /> : <Save />}
            onClick={saveTerritories}
            disabled={saving || territories.length === 0}
          >
            Save All Polygons
          </Button>
        </Box>
      </Drawer>

      {/* Main Content */}
      <main
        className={classes.content}
        style={{
          marginLeft: drawerOpen && !isMobile ? 0 : -DRAWER_WIDTH,
        }}
      >
        {/* Map container */}
        <div className={classes.mapWrapper}>
          <div ref={mapContainerRef} className={classes.mapContainer}></div>
        </div>
      </main>

      {/* Territory details dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Territory Details</DialogTitle>
        <DialogContent>
          <TextField
            label="Territory Name"
            value={territoryName}
            onChange={(e) => setTerritoryName(e.target.value)}
            fullWidth
            margin="normal"
            autoFocus
          />
          <FormControl fullWidth margin="normal">
            <InputLabel id="employee-select-label">Assigned Spray Tech</InputLabel>
            <Select
              labelId="employee-select-label"
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
            >
              <MenuItem value="">
                <em>Unassigned</em>
              </MenuItem>
              {sprayTechs.map(emp => (
                <MenuItem key={emp._id} value={emp._id}>
                  {emp.firstName} {emp.lastName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={deleteTerritory} color="secondary">
            Delete
          </Button>
          <Button onClick={() => setDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={assignTerritory} color="primary" variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
        message={notification.message}
      />
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    url: state.url
  }
}

export default connect(mapStateToProps)(HerbicideTerritoryMap);
